import Routes from "@navigations/Routes";
import {
  isEmbeddedFrame,
  parentResizesIframe,
  postIframeLayout,
} from "@utils/iframeMessaging";
import { isShownScreen, measureElementDocumentHeight } from "./contentBounds";

const MIN_CHANGE = 4;
const FILTER_ROOT_ID = "iframe-filter-root";

// scrollHeight follows a column stretched to the iframe. On a phone that
// leaves a band of page background under the filter buttons.
// The form can sit in a scroller that is still the height of the short results
// frame. Grow that scroller to its content before measuring, or the iframe
// stays at the results height.
const expandFilterScrollers = (root: HTMLElement) => {
  if (!parentResizesIframe()) return;
  root.querySelectorAll<HTMLElement>("div").forEach((element) => {
    if (element.clientHeight < 160) return;
    const style = window.getComputedStyle(element);
    const scrolls =
      style.overflowY === "auto" ||
      style.overflowY === "scroll" ||
      style.overflowY === "hidden";
    if (!scrolls) return;
    const contentHeight = element.scrollHeight;
    if (contentHeight <= element.clientHeight + 8) return;
    element.style.height = `${contentHeight}px`;
    element.style.maxHeight = "none";
    element.style.flexGrow = "0";
    element.style.flexShrink = "0";
    element.style.overflowY = "visible";
  });
};

const visibleFilterRoot = () => {
  const roots = document.querySelectorAll<HTMLElement>(`#${FILTER_ROOT_ID}`);
  return Array.from(roots).find((root) => isShownScreen(root)) ?? null;
};

const measureFilterScreenHeight = () => {
  const root = visibleFilterRoot();
  if (!root) return 0;
  expandFilterScrollers(root);
  return measureElementDocumentHeight(root);
};

export const initializeSearchFilterHeight = () => {
  if (!isEmbeddedFrame()) return () => undefined;

  let active = true;
  let lastPosted = 0;
  let frameId = 0;
  let settleTimer = 0;

  const publish = () => {
    if (!active) return;
    observeRoot();
    const height = measureFilterScreenHeight();
    if (height <= 0 || Math.abs(height - lastPosted) < MIN_CHANGE) return;
    lastPosted = height;
    postIframeLayout({
      mode: "auto-height",
      route: Routes.FilterScreen,
      height,
    });
  };

  const schedule = () => {
    cancelAnimationFrame(frameId);
    window.clearTimeout(settleTimer);
    frameId = requestAnimationFrame(() => {
      publish();
      // The cached filter screen is shown after the short results frame is
      // already in place. Measure again once that layout has settled.
      settleTimer = window.setTimeout(publish, 300);
    });
  };

  const observer = new MutationObserver(schedule);
  if (document.body) {
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["aria-hidden", "class", "hidden", "style"],
    });
  }
  const resizeObserver = new ResizeObserver(schedule);
  const observeRoot = () => {
    const root = visibleFilterRoot();
    if (root) resizeObserver.observe(root);
  };
  observeRoot();
  window.addEventListener("resize", schedule);
  schedule();

  return () => {
    active = false;
    cancelAnimationFrame(frameId);
    window.clearTimeout(settleTimer);
    observer.disconnect();
    resizeObserver.disconnect();
    window.removeEventListener("resize", schedule);
  };
};

export const cleanupSearchFilterHeight = (cleanup: () => void) => cleanup();
